/**
 * Monta a `option` do ECharts para a Visualização em Sunburst.
 *
 * Isolado do componente para poder ser testado sem montar canvas: a `option` é
 * um objeto puro, função dos nós e da paleta.
 *
 * Restrições do ECharts 5.0.1, que é a versão do projeto:
 * - `levels[].radius` só existe a partir do 5.2.0, então a espessura de cada
 *   anel é a divisão automática do `radius` da série;
 * - `emphasis.focus: 'relative'` só existe a partir do 5.6.0.
 */
import { getInkColor, mixHex } from './panelPalette';
import { sunburstLevels } from './panelSunburst';
import getSensorAbbreviation from './sensorLabel';

/** Opacidade de quem não casou o Filtro do Painel. */
export const DIMMED_OPACITY = 0.22;

/**
 * Ângulo mínimo, em graus, para o rótulo aparecer em cada anel.
 *
 * Regula sozinho a profundidade: no recorte da frota o Ativo tem ~2,4° e fica
 * mudo; ao entrar numa Instalação o mesmo anel passa de 10° e os nomes aparecem.
 */
const MIN_LABEL_ANGLE = {
  [sunburstLevels.COMPANY]: 8,
  [sunburstLevels.INSTALLATION]: 6,
  [sunburstLevels.ASSET]: 10,
  [sunburstLevels.SENSOR]: 10,
};

/**
 * Raio interno e externo da série, em porcentagem do raio disponível. O furo
 * interno comporta a trilha do drill-down.
 */
const INNER_RADIUS_PERCENT = 22;
const OUTER_RADIUS_PERCENT = 95;

/** Folga entre o texto e as bordas do anel, somando as duas pontas. */
const LABEL_PADDING_PX = 8;

/** Abaixo disso não cabem nem as reticências. */
const MIN_LABEL_WIDTH_PX = 12;

/**
 * Caracteres que cabem no círculo inteiro quando há um anel só.
 *
 * Só vale enquanto o gráfico não foi medido — na primeira pintura, antes de o
 * componente saber o próprio tamanho. Medido, o corte passa a ser em pixels
 * (`getLabelWidth`): uma conta de letras aferida num tamanho só estourava nos
 * gráficos menores e nos nomes de letras largas.
 */
const RADIAL_CHAR_BUDGET = 34;

const ELLIPSIS = '…';

/** Ordem dos anéis, de dentro para fora. */
const LEVEL_ORDER = [
  sunburstLevels.COMPANY,
  sunburstLevels.INSTALLATION,
  sunburstLevels.ASSET,
  sunburstLevels.SENSOR,
];

/**
 * Corta o texto que não cabe na célula, marcando o corte com reticências.
 *
 * O corte vale só para o desenho: o nó guarda o nome inteiro, e é ele que o
 * tooltip mostra. Sem isso um nome longo atravessa os anéis vizinhos e sai do
 * gráfico, como acontecia com "Linhas de Macapá Transmissora de Energia".
 *
 * @param {String} text Nome do nó.
 * @param {Number} maxChars Caracteres que cabem no anel.
 * @returns {String} Texto que cabe.
 */
export function truncateLabel(text, maxChars) {
  const value = String(text || '');
  const limit = Math.max(1, Math.floor(maxChars));

  return value.length <= limit
    ? value
    : `${value.slice(0, limit - 1).trimRight()}${ELLIPSIS}`;
}

/**
 * Largura disponível para o rótulo em cada anel, em pixels.
 *
 * Todo rótulo é radial, então o espaço de cada um é a espessura do anel: o
 * trecho entre o raio interno e o externo da série, dividido pelos anéis
 * visíveis. O ECharts mede o raio em relação ao menor lado do gráfico.
 *
 * @param {Object} size `{ width, height }` do gráfico, em pixels.
 * @param {Number} ringCount Anéis visíveis.
 * @returns {Number|null} Largura do rótulo, ou null sem medida.
 */
export function getLabelWidth(size, ringCount) {
  if (!size || !size.width || !size.height || !ringCount) {
    return null;
  }

  const radius = Math.min(size.width, size.height) / 2;
  const ringSpan = OUTER_RADIUS_PERCENT - INNER_RADIUS_PERCENT;
  const thickness = (radius * ringSpan) / (100 * ringCount);

  return Math.max(MIN_LABEL_WIDTH_PX, Math.floor(thickness - LABEL_PADDING_PX));
}

/**
 * Texto do setor. No anel de Sensor é a Sigla do Sensor, a mesma escrita na
 * célula da Barra na Grade: o nome completo não cabe na espessura do anel, e o
 * operador reconhece o Sensor pelo mesmo nome nas duas visualizações.
 */
function getLabelText(node) {
  return node.level === sunburstLevels.SENSOR
    ? getSensorAbbreviation(node.name)
    : node.name;
}

/** Metade dos Ativos em Atenção cai no âmbar, o meio da escala. */
const LOAD_MIDPOINT = 0.5;

/**
 * Quanto da cor da escala entra sobre a superfície nos anéis de agrupamento.
 *
 * Abaixo de 1 de propósito. A cor cheia é do anel de Ativo, logo acima: tingir o
 * agrupamento com o mesmo verde cheio confundiria os dois anéis.
 */
export const LOAD_TINT = 0.7;

/**
 * Cor da Carga de Atenção: verde sem nenhum Ativo em Atenção, âmbar na metade,
 * vermelho com todos.
 *
 * @param {Number} load Fração entre 0 e 1.
 * @param {Object} palette Saída de `readPanelPalette`.
 * @returns {String} Cor hexadecimal.
 */
export function getLoadColor(load, palette) {
  const upperHalf = 1 - LOAD_MIDPOINT;
  const ramp = load <= LOAD_MIDPOINT
      ? mixHex(palette.normal, palette.loadMid, load / LOAD_MIDPOINT)
      : mixHex(
          palette.loadMid,
          palette.attention,
          (load - LOAD_MIDPOINT) / upperHalf,
        );

  return mixHex(palette.surfaceRaised, ramp, LOAD_TINT);
}

/**
 * Cor do setor.
 *
 * Sensor usa a paleta de Status de Módulo e Ativo a de Estado do Ativo, ambas
 * idênticas às da Grade.
 * Empresa e Instalação usam a Carga de Atenção, do verde ao vermelho —
 * pior-vence satura nesses anéis e deixaria o centro vermelho para sempre. Nó
 * sem Ativo em operação fica cinza, e não verde: sem operação não há saúde a
 * afirmar. Ver docs/adr/0005-echarts-no-sunburst-do-painel.md
 *
 * @param {Object} node Nó da árvore do Sunburst.
 * @param {Object} palette Saída de `readPanelPalette`.
 * @returns {String} Cor hexadecimal do setor.
 */
export function getNodeColor(node, palette) {
  if (node.level === sunburstLevels.SENSOR) {
    return palette.sensors[node.status] || palette.sensors.indeterminate;
  }

  if (node.level === sunburstLevels.ASSET) {
    return palette.states[node.state] || palette.states.normal;
  }

  if (node.attentionLoad === null || node.attentionLoad === undefined) {
    return palette.loadEmpty;
  }

  return getLoadColor(node.attentionLoad, palette);
}

/**
 * @param {Object} fit `{ maxChars, labelWidth }`: com a largura medida, o corte
 *   é do ECharts, em pixels (ver `buildLevels`); sem ela, vale a conta de letras.
 */
function toSeriesData(node, palette, fit) {
  const color = getNodeColor(node, palette);
  const opacity = node.dimmed ? DIMMED_OPACITY : 1;
  const text = getLabelText(node);

  return {
    id: node.id,
    name: fit.labelWidth ? text : truncateLabel(text, fit.maxChars),
    value: node.value,
    // Guardado para o tooltip e para o clique: o ECharts devolve isto em
    // `params.data`, evitando uma segunda busca na árvore.
    node,
    // Tinta de maior contraste com a cor que de fato aparece: a do setor
    // composta sobre o fundo, já esmaecida quando não casa o filtro. Cor fixa
    // não serve — o texto claro caía para 1,5:1 no verde de Sensor Ativo.
    label: {
      color: getInkColor(color, palette.background, opacity) || palette.text,
    },
    itemStyle: {
      color,
      opacity,
      // Quem casou o filtro ganha contorno; a posição e o ângulo não mudam,
      // porque girar a frota inteira destruiria a memória espacial.
      borderColor: node.matched ? palette.match : palette.background,
      borderWidth: node.matched ? 2 : 1,
    },
    children: (node.children || []).map((child) => toSeriesData(child, palette, fit)),
  };
}

/** Quantos anéis a árvore recebida realmente tem. */
function treeDepth(nodes) {
  return (nodes || []).reduce(
    (deepest, node) => Math.max(deepest, 1 + treeDepth(node.children)),
    0,
  );
}

/**
 * Anéis visíveis, do mais interno para o mais externo.
 *
 * Limitado à profundidade real da árvore: os anéis ilegíveis já foram podados,
 * e contá-los aqui apertaria o rótulo dos que sobraram.
 */
function visibleLevels(nodes, firstLevel) {
  const start = LEVEL_ORDER.indexOf(firstLevel);
  const fromRoot = start < 0 ? LEVEL_ORDER : LEVEL_ORDER.slice(start);

  return fromRoot.slice(0, Math.max(treeDepth(nodes), 1));
}

/**
 * Configuração de rótulo de cada anel visível.
 *
 * Todos radiais: o rótulo tangencial disputa espaço com o arco, que varia de
 * setor para setor, enquanto o radial dispõe sempre da espessura do anel — igual
 * para todos os setores daquele anel, o que torna o corte previsível.
 *
 * Com a largura medida, o próprio ECharts corta o texto em pixels, com
 * reticências: mede cada letra, e nome em caixa alta ou de letras largas deixa
 * de estourar o anel.
 */
function buildLevels(levels, palette, labelWidth) {
  const fit = labelWidth ? { width: labelWidth, overflow: 'truncate' } : {};

  // O primeiro elemento de `levels` configura o nó raiz virtual, não um anel.
  return [{}].concat(
    levels.map((level) => ({
      label: {
        show: true,
        rotate: 'radial',
        minAngle: MIN_LABEL_ANGLE[level],
        color: palette.text,
        fontSize: level === sunburstLevels.COMPANY ? 12 : 11,
        fontWeight: level === sunburstLevels.COMPANY ? 700 : 400,
        ...fit,
      },
    })),
  );
}

/**
 * @param {Object} params `{ nodes, palette, formatTooltip, size }`. `size` é o
 *   `{ width, height }` medido do gráfico; ausente, o rótulo cai na conta de
 *   letras.
 * @returns {Object} Option pronta para o `e-chart`.
 */
export default function buildSunburstOption({
  nodes,
  palette,
  formatTooltip,
  size,
}) {
  const visible = nodes || [];
  const levels = visibleLevels(
    visible,
    visible.length ? visible[0].level : sunburstLevels.COMPANY,
  );
  // Quanto mais anéis, mais fino cada um, e menos texto cabe.
  const fit = {
    maxChars: RADIAL_CHAR_BUDGET / levels.length,
    labelWidth: getLabelWidth(size, levels.length),
  };

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: palette.surfaceRaised,
      borderColor: palette.border,
      textStyle: { color: palette.text, fontSize: 12 },
      formatter: (params) => formatTooltip(params.data && params.data.node),
    },
    series: [
      {
        type: 'sunburst',
        data: visible.map((node) => toSeriesData(node, palette, fit)),
        // O furo interno precisa comportar a trilha do drill-down, que fica sobre
        // ele como controle de HTML e nao como rotulo do grafico.
        radius: [`${INNER_RADIUS_PERCENT}%`, `${OUTER_RADIUS_PERCENT}%`],
        // O drill-down é controlado pelo componente, que refatia a árvore e
        // redesenha. O do ECharts guardaria um estado paralelo ao nosso, sem
        // como exibir a trilha no centro nem voltar sozinho por inatividade.
        nodeClick: false,
        // A ordem já vem resolvida da árvore; reordenar aqui giraria a frota.
        sort: null,
        animationDurationUpdate: 500,
        itemStyle: {
          borderColor: palette.background,
          borderWidth: 1,
        },
        emphasis: {
          itemStyle: {
            borderColor: palette.text,
            borderWidth: 3,
            shadowBlur: 12,
            shadowColor: palette.text,
          },
        },
        levels: buildLevels(levels, palette, fit.labelWidth),
      },
    ],
  };
}
